import React, { useRef, useEffect, useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import styled from 'styled-components';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import QuillToolbar, { formats, modules } from './RichTextEditorToolBar';
import { MAX_DESCRIPTION_LENGTH } from 'constants/product';
import { stripHtml } from 'utils/product';
import { BorderRadius } from 'styles/themeBorderRadius';
import FlexboxContainerComponent from 'components/UI/FlexboxContainer';
import { Main } from 'styles/themeColors';
import { useReadPermission } from 'hooks/useUserRoles';
import { PermissionCode } from 'data/UserPermissions';
import { ReadOnlyWrapper } from 'components/common/ReadOnlyWrapper';

export interface Props {
  onBlur: Function;
  value: string;
  disabled?: boolean;
  placeholder: string;
  isInvalid?: boolean;
  productRedesignFlag?: boolean;
  maxLength?: number;
  setDescriptionCharacters?: (length: number) => void;
  toolbarId?: string | null;
  showCharactersCount?: boolean;
  permissionCode?: PermissionCode;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 2em;
  width: 100%;
`;

const TextareaContainer = styled.div`
  position: relative;
  border-radius: ${BorderRadius.m};
`;

const LabelBottom = styled.div<any>`
  font-size: 15px;
  color: ${({ color }) => color ? color : Main.black};
`;

const RichTextEditorComponent = forwardRef<any, Props>(({ onBlur, value, disabled, placeholder, isInvalid, productRedesignFlag = false, maxLength = MAX_DESCRIPTION_LENGTH, toolbarId = null, showCharactersCount = false, permissionCode }, ref) => {
  const quillRef = useRef<ReactQuill>(null);
  const [content, setContent] = useState<string>('');
  const [descriptionCharacters, setDescriptionCharacters] = useState(maxLength);
  const { readOnly } = useReadPermission(permissionCode!);

  useEffect(() => {
    setContent(value);
  }, [value])

  useEffect(() => {
    // Check if the editor is fully instantiated before accessing it
    if (quillRef.current && (quillRef.current as any).editor) {
      const quill = quillRef.current.getEditor();

      const handleTextChange = () => {
        const plainText = stripHtml(quill.root.innerHTML);
        setDescriptionCharacters?.(plainText.length > maxLength ? 0 : maxLength - plainText.length);
      };

      quill.on('text-change', handleTextChange);

      // Cleanup listener on unmount or when dependencies change
      return () => {
        quill.off('text-change', handleTextChange);
      };
    }
  }, [content, maxLength]);

  // Global click handler to save when clicking outside the editor
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if click is outside the editor container
      const container = document.getElementById(
        productRedesignFlag ? "html-editor-container-redesign" : "html-editor-container"
      );

      if (container && !container.contains(event.target as Node)) {
        // Click was outside the editor, trigger blur and save
        if (quillRef.current && (quillRef.current as any).editor) {
          const quill = quillRef.current.getEditor();
          if (quill.hasFocus()) {
            quill.blur(); // This will trigger handleBlur which saves the content
          }
        }
      }
    };

    // Add global click listener
    document.addEventListener('mousedown', handleClickOutside);

    // Cleanup on unmount
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [productRedesignFlag]);

  // Fix Quill lists to use proper semantic HTML (<ul> for bullets, <ol> for numbers)
  const fixQuillLists = useCallback((html: string): string => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    const orderedLists = tempDiv.querySelectorAll('ol');

    orderedLists.forEach((ol) => {
      const bulletItems = ol.querySelectorAll('li[data-list="bullet"]');
      const orderedItems = ol.querySelectorAll('li[data-list="ordered"]');
      const hasBulletItems = bulletItems.length > 0;
      const hasOrderedItems = orderedItems.length > 0;

      if (hasBulletItems && hasOrderedItems) {
        // Mixed list - split into ul and ol
        const ul = document.createElement('ul');
        const newOl = document.createElement('ol');

        Array.from(ol.children).forEach(child => {
          if (child.tagName === 'LI') {
            const dataList = child.getAttribute('data-list');
            const isBullet = dataList === 'bullet';

            const clonedChild = child.cloneNode(true) as HTMLElement;
            clonedChild.removeAttribute('data-list');

            const qlUiSpans = clonedChild.querySelectorAll('span.ql-ui');
            qlUiSpans.forEach(span => span.remove());

            if (isBullet) {
              ul.appendChild(clonedChild);
            } else {
              newOl.appendChild(clonedChild);
            }
          }
        });

        const wrapper = document.createDocumentFragment();
        const firstIsBullet = ol.children[0]?.getAttribute('data-list') === 'bullet';

        if (firstIsBullet) {
          wrapper.appendChild(ul);
          if (newOl.children.length > 0) wrapper.appendChild(newOl);
        } else {
          if (newOl.children.length > 0) wrapper.appendChild(newOl);
          wrapper.appendChild(ul);
        }

        ol.replaceWith(wrapper);
      } else if (hasBulletItems) {
        // Only bullet items - convert to ul
        const ul = document.createElement('ul');

        Array.from(ol.attributes).forEach(attr => {
          if (attr.name !== 'data-list') {
            ul.setAttribute(attr.name, attr.value);
          }
        });

        while (ol.firstChild) {
          const li = ol.firstChild as HTMLElement;
          if (li.nodeType === Node.ELEMENT_NODE && li.tagName === 'LI') {
            li.removeAttribute('data-list');
            const qlUiSpans = li.querySelectorAll('span.ql-ui');
            qlUiSpans.forEach(span => span.remove());
          }
          ul.appendChild(li);
        }

        ol.replaceWith(ul);
      }
      // If only ordered items, leave as ol (no changes needed)
    });

    return tempDiv.innerHTML;
  }, []);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    getContentAndSave: () => {
      if (quillRef.current && (quillRef.current as any).editor) {
        const quill = quillRef.current.getEditor();
        // Fix the HTML and save
        const fixedHtml = fixQuillLists(quill.root.innerHTML);
        onBlur(fixedHtml);
        return fixedHtml;
      }
      return value;
    }
  }), [fixQuillLists, onBlur, value]);

  const handleChange = (newContent: string) => {
    setContent(newContent)
  }

  const handleBlur = () => {
    if (quillRef.current && (quillRef.current as any).editor) {
      const quill = quillRef.current.getEditor();
      // Fix the HTML and save only on blur
      const fixedHtml = fixQuillLists(quill.root.innerHTML);
      onBlur(fixedHtml);
    }
  }

  return (
    <>
      <ReadOnlyWrapper readOnly={readOnly}>
        <Container
          id={productRedesignFlag ? "html-editor-container-redesign" : "html-editor-container"}
          className={isInvalid ? 'invalid' : ''}
        >
          <QuillToolbar id={toolbarId || 'toolbar'} />
          <TextareaContainer>
            <ReactQuill
              ref={quillRef}
              value={content}
              onChange={handleChange}
              onBlur={handleBlur}
              id="html-editor"
              theme="snow"
              placeholder={placeholder}
              modules={modules(toolbarId || 'toolbar')}
              formats={formats}
              readOnly={readOnly || disabled}
              style={{ minHeight: productRedesignFlag ? '300px' : 'auto', borderRadius: BorderRadius.m }}
            />
          </TextareaContainer>
        </Container>
      </ReadOnlyWrapper>

      {showCharactersCount && <FlexboxContainerComponent flexDirection='row' gap='5px'>
        <LabelBottom>Characters remaining:</LabelBottom>
        <LabelBottom color={Main.brandDark}>{descriptionCharacters}</LabelBottom>
      </FlexboxContainerComponent>
      }
    </>
  );
});

export default RichTextEditorComponent;
